import React from 'react';
import { NavLink, useNavigate, Outlet } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faShieldAlt, faTachometerAlt, faUsers, faBuilding, faHandHoldingHeart, faHistory, faSignOutAlt, faUserFriends, faUserShield, faBars } from '@fortawesome/free-solid-svg-icons';
import { adminAuthAPI } from '../../services/adminAPI';
import { toast } from 'react-toastify';
import './admin.css';

const AdminLayout = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = React.useState(false);

  const closeSidebar = () => setSidebarOpen(false);
  const toggleSidebar = () => setSidebarOpen(!sidebarOpen);

  React.useEffect(() => {
    // add class to body so we can override global styles (overflow-x:hidden) when
    // within the admin panel—this allows inner containers to scroll correctly on mobile
    document.body.classList.add('admin-mode');
    const handleResize = () => {
      if (window.innerWidth > 768) {
        setSidebarOpen(false);
      }
    };
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      document.body.classList.remove('admin-mode');
    };
  }, []);

  const handleLogout = async () => {
    try {
      await adminAuthAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('admin_access_token');
      localStorage.removeItem('admin_refresh_token');
      localStorage.removeItem('admin_user');
      toast.success('Logged out successfully');
      navigate('/admin/login');
    }
  };

  const adminUser = JSON.parse(localStorage.getItem('admin_user') || '{}');

  return (
    <div className="admin-layout">
      {sidebarOpen && (
        <div className="admin-overlay open" onClick={closeSidebar}></div>
      )}
      
      <button 
        className="admin-mobile-toggle" 
        onClick={toggleSidebar}
        title="Toggle Menu"
        aria-label="Toggle navigation menu"
      >
        <FontAwesomeIcon icon={faBars} style={{ marginRight: '0.5rem' }} />
        <span>Menu</span>
      </button>

      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="admin-sidebar-header">
          <h2>
            <FontAwesomeIcon icon={faShieldAlt} />
            Super Admin
          </h2>
          <p>Welcome, {adminUser.username || 'Admin'}</p>
        </div>

        <nav>
          <ul className="admin-nav">
            <li className="admin-nav-item">
              <NavLink
                to="/admin/dashboard"
                onClick={closeSidebar}
                className={({ isActive }) =>
                  `admin-nav-link ${isActive ? 'active' : ''}`
                }
              >
                <FontAwesomeIcon icon={faTachometerAlt} />
                Dashboard
              </NavLink>
            </li>
            <li className="admin-nav-item">
              <NavLink
                to="/admin/users"
                onClick={closeSidebar}
                className={({ isActive }) =>
                  `admin-nav-link ${isActive ? 'active' : ''}`
                }
              >
                <FontAwesomeIcon icon={faUsers} />
                Users
              </NavLink>
            </li>
            <li className="admin-nav-item">
              <NavLink
                to="/admin/companies"
                onClick={closeSidebar}
                className={({ isActive }) =>
                  `admin-nav-link ${isActive ? 'active' : ''}`
                }
              >
                <FontAwesomeIcon icon={faBuilding} />
                Companies
              </NavLink>
            </li>
            <li className="admin-nav-item">
              <NavLink
                to="/admin/donations"
                onClick={closeSidebar}
                className={({ isActive }) =>
                  `admin-nav-link ${isActive ? 'active' : ''}`
                }
              >
                <FontAwesomeIcon icon={faHandHoldingHeart} />
                Donations
              </NavLink>
            </li>
            <li className="admin-nav-item">
              <NavLink
                to="/admin/facilitators"
                onClick={closeSidebar}
                className={({ isActive }) =>
                  `admin-nav-link ${isActive ? 'active' : ''}`
                }
              >
                <FontAwesomeIcon icon={faUserFriends} />
                Facilitators
              </NavLink>
            </li>
            <li className="admin-nav-item">
              <NavLink
                to="/admin/audit-logs"
                onClick={closeSidebar}
                className={({ isActive }) =>
                  `admin-nav-link ${isActive ? 'active' : ''}`
                }
              >
                <FontAwesomeIcon icon={faHistory} />
                Audit Logs
              </NavLink>
            </li>
            <li className="admin-nav-item">
              <NavLink
                to="/admin/profile"
                onClick={closeSidebar}
                className={({ isActive }) =>
                  `admin-nav-link ${isActive ? 'active' : ''}`
                }
              >
                <FontAwesomeIcon icon={faUserShield} />
                My Profile
              </NavLink>
            </li>
          </ul>
        </nav>

        <button className="admin-logout-btn" onClick={() => { closeSidebar(); handleLogout(); }}>
          <FontAwesomeIcon icon={faSignOutAlt} />
          Logout
        </button>
      </aside>

      <main className="admin-main">
        <Outlet />
      </main>
    </div>
  );
};

export default AdminLayout;
